/**
 * Defined in (YAG-1064): the sibling repository whose compose file starts
 * this one's services. Setup fills it in when a compose file in another of
 * the workspace's repositories names this one; a person can set it, change it
 * or clear it here. Every edit here is the person's, so setup never
 * overwrites it, and a clear sticks: setup does not look again.
 *
 * The sibling is checked out beside this repository like any other
 * repository under What agents read, so choosing one adds it there too, and
 * clearing it takes it away again when nothing else uses it.
 */
import { useState } from "react";
import type { QaEnvironmentConfig } from "@yagni/shared";
import { Button, Input } from "../ui/index.js";
import { LinesControl, Row, RowLink } from "./environmentControls.js";
import type { RepositoryChoice } from "./EnvironmentConfigRows.js";

export type EnvironmentFrom = NonNullable<QaEnvironmentConfig["environmentFrom"]>;
type ManagedKind = "postgres" | "redis" | "mysql";

/** What Find again answers: the sibling, its default branch, and the managed services its compose file replaces. */
export interface DetectedSibling {
  repoId: string;
  fullName: string;
  defaultBranch: string;
  environmentFrom: EnvironmentFrom;
  managed: ManagedKind[];
}

/** Find again's answer: the sibling, or null, and how far the server read before it answered. */
export interface SiblingSearch {
  found: DetectedSibling | null;
  /**
   * Absent from a server from before it said; `cutShort` when the budget ran
   * out first, `failed` when nothing could be read at all.
   */
  walk?: { read: number; total: number; cutShort: boolean; failed?: boolean };
}

export const SIBLINGS_UNREADABLE = "The other repositories could not be read just now. Try again.";

/** The note when Find again found nothing: whether it read every repository says how sure it is. */
export function siblingNotFoundNote(walk: SiblingSearch["walk"]): string {
  if (walk?.failed) return SIBLINGS_UNREADABLE;
  if (walk?.cutShort)
    return `Read ${walk.read} of ${walk.total} repositories before time ran out; none names this one. Try again.`;
  return "No compose file in the workspace's other repositories names this one.";
}

const UNRESOLVED_SHA = "0".repeat(40);
const MAX_DEPENDENCIES = 20;
const DEFAULT_COMPOSE_FILE = "docker-compose.yml";
export const NO_ROOM =
  "Twenty repositories are already checked out beside this one. Remove one under Other repositories first.";

function repositoryName(repoId: string, repositories: readonly RepositoryChoice[]): string {
  const found = repositories.find((repo) => repo.id === repoId);
  return found ? `${found.owner}/${found.name}` : "Repository unavailable";
}

/** The Defined in row's summary. */
export function environmentFromSummary(
  config: Pick<QaEnvironmentConfig, "environmentFrom">,
  repositories: readonly RepositoryChoice[],
): string {
  const from = config.environmentFrom;
  if (from === null) return "Cleared by hand. Setup will not look again.";
  if (!from) return "This repository alone";
  const starts = from.services.length ? `starts ${from.services.join(", ")}` : "starts nothing yet";
  return `Environment defined in ${repositoryName(from.repoId, repositories)} · ${from.composeFile} · ${starts}`;
}

/**
 * Whether a checkout is there only for the environment: no commands, no
 * variables of its own, and no service runs from it. Only such a checkout
 * goes when the environment stops being defined in it.
 */
function onlyForEnvironment(config: QaEnvironmentConfig, repoId: string): boolean {
  const dependency = config.dependencies?.find((item) => item.repoId === repoId);
  return Boolean(
    dependency &&
      !dependency.setupCommands.length &&
      !Object.keys(dependency.environmentDefaults ?? {}).length &&
      !config.services.some((service) => service.repoId === repoId),
  );
}

function withoutCheckout(config: QaEnvironmentConfig, repoId: string | undefined) {
  const dependencies = config.dependencies ?? [];
  return repoId && onlyForEnvironment(config, repoId)
    ? dependencies.filter((item) => item.repoId !== repoId)
    : [...dependencies];
}

/**
 * The patch that defines the environment in `from`: the sibling checked out
 * beside this one (its commit resolves when the environment is saved), the
 * managed services its compose file starts its own copy of turned off, and
 * the browser service named, which a checkout beside this one requires. Null
 * when twenty repositories are already checked out beside this one.
 */
export function environmentFromPatch(
  config: QaEnvironmentConfig,
  from: EnvironmentFrom,
  options: { ref?: string; managed?: readonly ManagedKind[] } = {},
): Partial<QaEnvironmentConfig> | null {
  const previous = config.environmentFrom?.repoId;
  const dependencies =
    previous && previous !== from.repoId
      ? withoutCheckout(config, previous)
      : [...(config.dependencies ?? [])];
  if (!dependencies.some((item) => item.repoId === from.repoId)) {
    if (dependencies.length >= MAX_DEPENDENCIES) return null;
    dependencies.push({
      repoId: from.repoId,
      ref: options.ref ?? "main",
      sha: UNRESOLVED_SHA,
      pin: "fixed",
      setupCommands: [],
    });
  }
  const patch: Partial<QaEnvironmentConfig> = { environmentFrom: from, dependencies };
  for (const kind of options.managed ?? []) patch[kind] = null;
  if (config.target === "sandbox" && config.services.length && !config.browserService)
    patch.browserService = config.services[0]!.name;
  return patch;
}

/** The patch that clears it for good: setup will not look again, and the sibling's checkout goes when nothing else uses it. */
export function clearedEnvironmentFromPatch(
  config: QaEnvironmentConfig,
): Partial<QaEnvironmentConfig> {
  return {
    environmentFrom: null,
    dependencies: withoutCheckout(config, config.environmentFrom?.repoId),
  };
}

/** Service names as typed: trimmed, once each, never this repository's own. */
function serviceNames(lines: readonly string[], appService: string | null): string[] {
  return [...new Set(lines.map((line) => line.trim()).filter(Boolean))].filter(
    (name) => name !== appService,
  );
}

export function EnvironmentFromFields({
  config,
  repositories,
  primaryRepoId,
  onChange,
  onFind,
  onOpenRow,
}: {
  config: QaEnvironmentConfig;
  repositories: readonly RepositoryChoice[];
  primaryRepoId: string;
  onChange: (patch: Partial<QaEnvironmentConfig>) => void;
  /** Asks the server to look now; `found` is null when no sibling's compose file names this repository. */
  onFind: () => Promise<SiblingSearch>;
  /** Opens another settings row by its section key. */
  onOpenRow?: (section: string) => void;
}) {
  const [finding, setFinding] = useState(false);
  const [note, setNote] = useState("");
  const from = config.environmentFrom ?? null;
  const choices = repositories.filter((repo) => repo.id !== primaryRepoId);
  /** A person's edit: detection never touches it again. */
  const edit = (patch: Partial<EnvironmentFrom>) => {
    if (!from) return;
    onChange({ environmentFrom: { ...from, ...patch, source: "person" } });
  };
  const choose = (repoId: string) => {
    if (!choices.some((repo) => repo.id === repoId) || repoId === from?.repoId) return;
    const next: EnvironmentFrom = from
      ? { ...from, repoId, source: "person" }
      : {
          repoId,
          composeFile: DEFAULT_COMPOSE_FILE,
          services: [],
          appService: null,
          env: {},
          needs: [],
          ports: [],
          source: "person",
        };
    const patch = environmentFromPatch(config, next);
    setNote(patch ? "" : NO_ROOM);
    if (patch) onChange(patch);
  };
  const find = async () => {
    setFinding(true);
    setNote("");
    try {
      const { found, walk } = await onFind();
      if (!found) {
        setNote(siblingNotFoundNote(walk));
        return;
      }
      const patch = environmentFromPatch(config, found.environmentFrom, {
        ref: found.defaultBranch,
        managed: found.managed,
      });
      if (!patch) {
        setNote(NO_ROOM);
        return;
      }
      onChange(patch);
      setNote(`Found in ${found.fullName}. Save or run setup to keep it.`);
    } catch {
      setNote(SIBLINGS_UNREADABLE);
    } finally {
      setFinding(false);
    }
  };
  const variableNames = Object.keys(from?.env ?? {}).sort();

  return (
    <>
      <Row
        title="Repository"
        help="The repository whose compose file starts this one's services. It is checked out beside this one and nothing is ever written to it."
      >
        <span className="wrk-srow-controls">
          <select
            className="wrk-control"
            aria-label="Repository the environment is defined in"
            value={from?.repoId ?? ""}
            onChange={(event) => choose(event.target.value)}
          >
            <option value="" disabled>
              Choose a repository
            </option>
            {choices.map((repo) => (
              <option key={repo.id} value={repo.id}>
                {repo.owner}/{repo.name}
              </option>
            ))}
          </select>
          <Button variant="secondary" disabled={finding} onClick={() => void find()}>
            {finding ? "Looking" : "Find again"}
          </Button>
          {config.environmentFrom !== null && (
            <Button
              variant="ghost"
              onClick={() => {
                setNote("");
                onChange(clearedEnvironmentFromPatch(config));
              }}
            >
              Clear
            </Button>
          )}
          <span className="qa-note" role="status">
            {note}
          </span>
        </span>
      </Row>
      {from && (
        <>
          <Row
            title="Compose file"
            help="A path inside that repository, such as docker-compose.yml."
          >
            <span className="wrk-srow-controls">
              <Input
                aria-label="Compose file"
                value={from.composeFile}
                onChange={(event) => edit({ composeFile: event.target.value })}
              />
            </span>
          </Row>
          <Row
            title="Services it starts"
            help="One per line, started before the app and stopped with the sandbox."
          >
            <span className="wrk-srow-controls">
              <LinesControl
                label="Services it starts"
                value={from.services}
                onCommit={(lines) => {
                  const services = serviceNames(lines, from.appService);
                  edit({
                    services,
                    ports: from.ports.filter((port) => services.includes(port.service)),
                  });
                }}
              />
            </span>
          </Row>
          <Row
            title="This repository's service"
            help="Its service in the file, if it has one. The app itself always runs from this repository's checkout."
          >
            <span className="wrk-srow-controls">
              <Input
                aria-label="This repository's service"
                value={from.appService ?? ""}
                onChange={(event) => {
                  const appService = event.target.value.trim() || null;
                  const services = serviceNames(from.services, appService);
                  edit({
                    appService,
                    services,
                    ports: from.ports.filter((port) => services.includes(port.service)),
                  });
                }}
              />
            </span>
          </Row>
          <Row title="Variables from the file">
            <span className="wrk-srow-controls qa-note">
              {variableNames.length ? variableNames.join(", ") : "None"}
            </span>
          </Row>
          <Row title="What it expects from the host">
            <span className="wrk-srow-controls qa-note">
              {from.needs.length ? (
                <>
                  {from.needs.join(", ")}.{" "}
                  <RowLink section="credentials" onOpenRow={onOpenRow}>
                    Set them under Variables.
                  </RowLink>
                </>
              ) : (
                "Nothing"
              )}
            </span>
          </Row>
        </>
      )}
    </>
  );
}
